import {
  type ImgHTMLAttributes,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/cn";

/**
 * Progressive image reveal: a tinted frame reserves the space, then the real
 * pixels blur-up into place (opacity + blur + a settle from 1.04 scale).
 * Images already in the browser cache render instantly with no animation,
 * and prefers-reduced-motion collapses the reveal to a short fade.
 */
export interface SmoothImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Classes for the reserving frame: size, aspect ratio, rounding, position. */
  frameClassName?: string;
  /**
   * Set on images that are already in the first viewport. Lazy is right for a
   * long grid and wrong for its first row: those decode late and the page paints
   * with empty frames where the content should be.
   */
  priority?: boolean;
  /**
   * Skip the house grade (`--media-grade`). For pixels that must stay true:
   * a member's own photo, a filter preview, a logo.
   */
  ungraded?: boolean;
}

export function SmoothImage({
  frameClassName,
  className,
  onLoad,
  alt,
  priority = false,
  ungraded = false,
  ...imgProps
}: SmoothImageProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [phase, setPhase] = useState<"waiting" | "revealing" | "instant">("waiting");

  useEffect(() => {
    const el = ref.current;
    // Already decoded (cache hit): show immediately, a reveal seen dozens of
    // times a day must not animate.
    if (el?.complete && el.naturalWidth > 0) setPhase("instant");
  }, []);

  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      setPhase((p) => (p === "waiting" ? "revealing" : p));
      onLoad?.(event);
    },
    [onLoad],
  );

  return (
    // The grade sits on the frame, not the img, so it composes with the
    // blur-up reveal instead of replacing its filter.
    <span
      className={cn(
        "block overflow-hidden bg-surface-canvas",
        !ungraded && "[filter:var(--media-grade)]",
        frameClassName,
      )}
    >
      <img
        ref={ref}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        {...(priority ? { fetchPriority: "high" as const } : {})}
        {...imgProps}
        alt={alt ?? ""}
        onLoad={handleLoad}
        className={cn(
          "block h-full w-full object-cover",
          phase !== "instant" &&
            "[transition:opacity_400ms_var(--ease-out-expo),filter_500ms_var(--ease-out-expo),transform_900ms_var(--ease-out-expo)] motion-reduce:[transition:opacity_200ms_ease-out]",
          phase === "waiting"
            ? "scale-[1.04] opacity-0 blur-md motion-reduce:scale-100 motion-reduce:blur-0"
            : "scale-100 opacity-100 blur-0",
          className,
        )}
      />
    </span>
  );
}
